import type { GivingContentObjectDetail, GivingInterval, GivingTypeEnum, LabelInValue, UploadedFile } from 'models';
import { useCallback, useMemo, useState } from 'react';
import { useUser } from 'features/auth';
import { useGivingCreate } from '../../../../hooks';
import type { CreateGiveNowValues } from '../../give-now-option';
import { GIVE_NOW_SESSION_STORAGE_KEY } from './constants';
import { useValidatedGiveNowStructure } from './validated-give-now-structure';

export type GiveNowSessionStorage = {
  addItem: (newItem: SessionStorageGivingItem) => void;
  clearViewerItems: () => void;
  createItem: (formValues: CreateGiveNowValues, giving: GivingContentObjectDetail) => SessionStorageGivingItem;
  getItem: (itemId: string) => SessionStorageGivingItem | undefined;
  giveNow: SessionStorageGiveNow;
  removeItem: (itemId: string) => void;
  updateItem: (itemId: string, updatedItem: SessionStorageGivingItem) => void;
  viewerItems: SessionStorageGivingItem[];
};

export type SessionStorageGiveNow = Record<string, SessionStorageGivingItem[]>;

export type SessionStorageGivingItem = {
  amount: number;
  coverPhoto?: UploadedFile;
  displayName: boolean;
  endTime?: Date;
  id: string;
  name: string;
  organization: LabelInValue;
  priceId: string;
  recurring?: GivingInterval;
  showCustomAmount: boolean;
  type: GivingTypeEnum;
};

export const useGiveNowSessionStorage = (): GiveNowSessionStorage => {
  const { railsUserData: viewer } = useUser();
  const validatedGiveNowStructure = useValidatedGiveNowStructure();
  const [giveNow, setGiveNow] = useState(validatedGiveNowStructure());
  const { getPriceId } = useGivingCreate();

  const setGiveNowToSessionStorage = useCallback((updatedGiveNow: SessionStorageGiveNow) => {
    sessionStorage.setItem(GIVE_NOW_SESSION_STORAGE_KEY, JSON.stringify(updatedGiveNow));
  }, []);

  const updateGiveNow = useCallback(
    (updatedGiveNow: SessionStorageGiveNow) => {
      setGiveNow(updatedGiveNow);
      setGiveNowToSessionStorage(updatedGiveNow);
    },
    [setGiveNowToSessionStorage],
  );

  const createItem = useCallback(
    (formValues: CreateGiveNowValues, givingContentObject: GivingContentObjectDetail): SessionStorageGivingItem => {
      const { giving, organization } = givingContentObject;
      const amount = formValues.showCustomAmount ? formValues.customAmount : formValues.amount;

      return {
        amount,
        coverPhoto: giving.coverPhoto,
        displayName: formValues.displayName,
        endTime: giving.endTime,
        id: giving.id,
        name: giving.name,
        organization: { label: organization.name, value: organization.id },
        priceId: getPriceId({
          amount,
          giving,
          recurringValue: formValues.showCustomAmount ? undefined : formValues.recurring,
        }),
        recurring: formValues.recurring,
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
        showCustomAmount: formValues.showCustomAmount ?? false,
        type: giving.type,
      };
    },
    [getPriceId],
  );

  const addItem = useCallback(
    (newItem: SessionStorageGivingItem) => {
      const viewerId = viewer.id;
      const updatedGiveNow = {
        ...giveNow,
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
        [viewerId]: giveNow?.[viewerId] ? [...giveNow[viewerId], newItem] : [newItem],
      };
      updateGiveNow(updatedGiveNow);
    },
    [giveNow, updateGiveNow, viewer.id],
  );

  const updateItem = useCallback(
    (itemId: string, updatedItem: SessionStorageGivingItem) => {
      const viewerId = viewer.id;
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
      if (giveNow?.[viewerId]) {
        const updatedItems = giveNow[viewerId].map((item) => (item.id === itemId ? updatedItem : item));
        const updatedGiveNow = { ...giveNow, [viewerId]: updatedItems };
        updateGiveNow(updatedGiveNow);
      }
    },
    [giveNow, updateGiveNow, viewer.id],
  );

  const getItem = useCallback(
    (itemId: string) => {
      const viewerId = viewer.id;
      return giveNow[viewerId]?.find((item) => item.id === itemId);
    },
    [giveNow, viewer.id],
  );

  const removeItem = useCallback(
    (itemId: string) => {
      const viewerId = viewer.id;
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
      if (giveNow?.[viewerId]) {
        const filteredItems = giveNow[viewerId].filter((item) => item.id !== itemId);
        const updatedGiveNow = { ...giveNow, [viewerId]: filteredItems };
        updateGiveNow(updatedGiveNow);
      }
    },
    [giveNow, updateGiveNow, viewer.id],
  );

  const viewerItems = useMemo(() => giveNow[viewer.id] ?? [], [giveNow, viewer.id]);

  const clearViewerItems = useCallback(() => {
    const { [viewer.id]: _, ...otherViewers } = giveNow;
    updateGiveNow(otherViewers);
  }, [giveNow, updateGiveNow, viewer.id]);

  return useMemo(
    () => ({ addItem, clearViewerItems, createItem, getItem, giveNow, removeItem, updateItem, viewerItems }),
    [giveNow, addItem, removeItem, clearViewerItems, createItem, getItem, updateItem, viewerItems],
  );
};
